import { useState, useMemo, useEffect, useRef } from 'react';

import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import { useWishlistToggle } from '@/features/wishlist/presentation/hooks/useWishlistToggle';
import { useCustomizationSheet } from '@/features/customization/presentation/hooks/useCustomizationSheet';
import { PosCartService } from '../../application/services/PosCartService';
import { parseSizeList, findSizeOption } from '@/features/customization/utils/parseSizeList';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';
import type { PosCartItem } from '../../domain/models/PosCartItem';
import { parsePrice } from '@/shared/utils/priceFormatter';
import { facebookAddToWishlist } from '@/features/wishlist/presentation/analytics/facebookAddToWishlist';
import { submitBuyNowForm } from '@/features/pdp/utils/submitBuyNowForm';

interface UseStoreSelectionsArgs {
  pageName: 'Home' | 'PDP';
  onLoadMore?: () => void;
  hasMore?: boolean;
}

import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';

export function useStoreSelections(
  service: PosCartService,
  { pageName, onLoadMore, hasMore }: UseStoreSelectionsArgs
) {

  const { homePageVariant: pageVariant } = useMultivariateExperiment();
  const sliderRef = useRef<HTMLDivElement>(null);
  const { analytics, isInitialized } = useAnalytics();

  const [selectedProduct, setSelectedProduct] = useState<PosCartItem | null>(null);
  const [isLoadingProductDetail, setIsLoadingProductDetail] = useState(false);
  const [loadedProductDetail, setLoadedProductDetail] = useState<ProductDetail | null>(null);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [isCartFullPopupOpen, setIsCartFullPopupOpen] = useState(false);

  const { userContext, isLoading: isUserContextLoading } = useUserContext();
  const { isWishlisted: getIsWishlisted, toggleWishlist } = useWishlistToggle();

  const isSelectedProductWishlisted = useMemo(() => {
    return selectedProduct ? getIsWishlisted(selectedProduct.designId) : false;
  }, [selectedProduct, getIsWishlisted]);

  const handleWishlistToggle = (product: PosCartItem, isWishlisted: boolean) => {
    if (!analytics || !isInitialized) return;

    const eventName = isWishlisted
      ? 'Store-Visit Item Added to Wishlist'
      : 'Store-Visit Item Removed from Wishlist';

    analytics.trackEvent(eventName, {
      'Page Name': pageName,
      'Source': product.sourceType,
      'Product Id': product.designId,
      'Product Name': product.designName,
      'Selling Price': parsePrice(product.discountedPrice ?? product.price),
      'Section': 'Store-visit Items',
    });
  };

  const handleAddToWishlistFromPopup = async () => {
    if (selectedProduct) {
      const success = await toggleWishlist(selectedProduct.designId);
      if (success) {
        handleWishlistToggle(selectedProduct, true);
        facebookAddToWishlist({
          designId: selectedProduct.designId,
          designName: selectedProduct.designName,
          designCategory: selectedProduct.categoryType,
          price: selectedProduct.discountedPrice ?? selectedProduct.price,
          variantId: selectedProduct.customizationId,
        });
      }
    }
  };



  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || !onLoadMore || !hasMore) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = slider;
      if (scrollLeft + clientWidth >= scrollWidth - 300) {
        onLoadMore();
      }
    };

    slider.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      slider.removeEventListener('scroll', handleScroll);
    };
  }, [onLoadMore, hasMore]);

  const customizationContext = useMemo(() => {
    if (!loadedProductDetail) return null;
    return {
      designId: String(loadedProductDetail.designId),
      designName: loadedProductDetail.designName,
      category: loadedProductDetail.category,
      customizationOptions: loadedProductDetail.customizationOptions,
      metal: loadedProductDetail.metal,
      goldPurity: loadedProductDetail.goldPurity,
      diamondStoneType: loadedProductDetail.diamondStoneType,
      solitaireCarat: loadedProductDetail.solitaireCarat,
      solitaireClarity: loadedProductDetail.solitaireClarity,
      solitaireColor: loadedProductDetail.solitaireColor,
      customizationId: loadedProductDetail.customizationId,
      size: loadedProductDetail.size,
      discountedPrice: loadedProductDetail.discountedPrice,
      isSizeable: loadedProductDetail.isSizeableProduct,
      nddAvailableSizes: loadedProductDetail.nddAvailableSizes,
    };
  }, [loadedProductDetail]);

  const sizeList = useMemo(() => {
    if (!loadedProductDetail) return [];
    return parseSizeList(loadedProductDetail.customizationOptions);
  }, [loadedProductDetail]);

  const selectedSize = useMemo(() => {
    if (!loadedProductDetail || !loadedProductDetail.size) return null;
    return findSizeOption(sizeList, loadedProductDetail.size);
  }, [loadedProductDetail, sizeList]);

  const sheet = useCustomizationSheet({
    service: service.getCustomizationService(),
    context: customizationContext,
    selectedSize,
    isOpen: isCustomizationOpen,
    onClose: () => {
      setIsCustomizationOpen(false);
      setLoadedProductDetail(null);
      setSelectedProduct(null);
    },
  });

  const handleAddToCart = async (product: PosCartItem) => {
    if (analytics && isInitialized) {
      analytics.trackEvent('Store-Visit Item Added To Cart', {
        'Page Name': pageName,
        'Source': product.sourceType,
        'Product Name': product.designName,
        'Product Id': product.designId,
        'Selling Price': parsePrice(product.discountedPrice ?? product.price),
      });
    }

    const cartCount = userContext?.cartCount ?? 0;
    const maxCartItems = userContext?.maxCartItemsCount ?? 3;
    if (isUserContextLoading || cartCount >= maxCartItems) {
      setSelectedProduct(product);
      setIsCartFullPopupOpen(true);
      return;
    }

    setSelectedProduct(product);
    setIsLoadingProductDetail(true);
    try {
      const detail = await service.getProductDetail(String(product.designId), {
        customizationId: product.customizationId,
        size: product.defaultSize,
      });
      setLoadedProductDetail(detail);
      setIsCustomizationOpen(true);
    } catch (err) {
      console.error('Failed to load customization details:', err);
    } finally {
      setIsLoadingProductDetail(false);
    }
  };

  const handleApplyCustomization = async () => {
    const cartCount = userContext?.cartCount ?? 0;
    const maxCartItems = userContext?.maxCartItemsCount ?? 3;
    if (isUserContextLoading || cartCount >= maxCartItems) {
      setIsCustomizationOpen(false);
      setLoadedProductDetail(null);
      setIsCartFullPopupOpen(true);
      return;
    }

    const result = await sheet.handleApply();
    if (result) {
      const priceVal = result.customization.price || 0;
      const discPriceVal = result.customization.discountedPrice || 0;
      const sellingPrice = priceVal > 0 && discPriceVal > 0 
        ? Math.min(priceVal, discPriceVal) 
        : (priceVal || discPriceVal || null);

      if (selectedProduct && analytics && isInitialized) {
        analytics.trackEvent('Store-Visit Item Proceed To Cart', {
          'Page Name': pageName,
          'Source': selectedProduct.sourceType,
          'Product Name': selectedProduct.designName,
          'Product Id': selectedProduct.designId,
          'Selling Price': result.customization.discountedPrice ?? result.customization.price,
        });
      }

      setIsCustomizationOpen(false);
      setLoadedProductDetail(null);
      setSelectedProduct(null);

      submitBuyNowForm({
        customizationId: result.customization.customizationId,
        sizeSelected: result.selectedSize?.key || '',
        discountedPrice: sellingPrice,
      });
    }
  };

  const trackItemClick = (product: PosCartItem) => {
    if (!analytics || !isInitialized) return;

    analytics.trackEvent('Store-Visit Item Clicked', {
      'Page Name': pageName,
      'Source': product.sourceType,
      'Product Id': product.designId,
      'Product Name': product.designName,
      'Selling Price': parsePrice(product.discountedPrice ?? product.price),
      'Section': 'Store-visit Items',
    });
  };

  const handleGoToCart = (product: PosCartItem) => {
    if (analytics && isInitialized) {
      analytics.trackEvent('Store-Visit Item Go To Cart', {
        'Page Name': pageName,
        'Product Id': product.designId,
        'Product Name': product.designName,
        'Selling Price': parsePrice(product.discountedPrice ?? product.price),
      });
    }
    trackItemClick(product);
    window.location.href = '/order';
  };

  const handleProductClick = (product: PosCartItem) => {
    trackItemClick(product);
  };

  const getTitleText = () => {
    if (pageName === 'PDP') {
      return 'Your In-Store Selections';
    }
    return 'Designs Picked by You';
  };

  return {
    sliderRef,
    selectedProduct,
    isLoadingProductDetail,
    loadedProductDetail,
    isCustomizationOpen,
    isCartFullPopupOpen,
    setIsCartFullPopupOpen,
    isSelectedProductWishlisted,
    handleAddToWishlistFromPopup,
    handleWishlistToggle,
    sheet,
    handleAddToCart,
    handleApplyCustomization,
    handleGoToCart,
    handleProductClick,
    pageVariant,
    getTitleText,
    cartCount: userContext?.cartCount ?? 0,
    maxCartItemsCount: userContext?.maxCartItemsCount ?? 3,
  };
}
